import { createFileRoute } from "@tanstack/react-router";
import { ExternalUrls } from "../lib/constants";
import { useSEO, useBreadcrumbSchema, PageSEO } from "../lib/seo";
import { usePostHog } from "../components/widgets/PostHogProvider";
import { Box, Container, Typography, Button, Stack, Grid } from "@mui/material";
import { Link } from "@tanstack/react-router";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ScienceIcon from "@mui/icons-material/Science";
import PsychologyIcon from "@mui/icons-material/Psychology";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import GroupsIcon from "@mui/icons-material/Groups";
import ForumIcon from "@mui/icons-material/Forum";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { Section } from "../components/ui/Section";
import { SectionHeader } from "../components/ui/SectionHeader";
import { InfoCard } from "../components/ui/InfoCard";
import { FeatureIcon } from "../components/ui/FeatureIcon";
import { LogoMarqueeSection } from "../components/sections/LogoMarqueeSection";
import { MetricsSection } from "../components/sections/MetricsSection";
import { TestimonialsSection } from "../components/sections/TestimonialsSection";
import { HeroMockup } from "../components/sections/HeroMockup";
import { BrandBackdrop } from "../components/brand/BrandBackdrop";
import { ScreenshotShowcase } from "../components/ui/ScreenshotShowcase";

import { CallToActionCard } from "../components/ui/CallToActionCard";
import { TwoColumnSection } from "../components/ui/TwoColumnSection";
import { FadeIn } from "../components/ui/FadeIn";
import { BrandColors } from "@neumind/shared/branding";

export const Route = createFileRoute("/")({
  component: HomePage,
});

function HomePage() {
  useSEO(PageSEO.home);
  useBreadcrumbSchema([{ name: 'Home', url: '/' }]);
  const { capture } = usePostHog();

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Brand-atmosphere layer: cool mesh wash + Care Circle constellation
            (signature motif) fading from the top-right shoulder. Subtle per the
            visual-language brief — blue ≤10%, cyan ≤6%. */}
        <BrandBackdrop
          mesh="light"
          constellation
          constellationCorner="top-right"
          constellationOpacity={0.6}
        />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: { xs: "center", md: "left" }, pb: { xs: 4, md: 8 } }}>
                <FadeIn direction="up" duration={800}>
                  <Typography variant="h1" sx={{ mb: 3 }}>
                    Extend Care{' '}
                    <Box component="span" sx={{ color: "primary.main" }}>
                      Beyond the Clinic
                    </Box>
                  </Typography>
                </FadeIn>
                <FadeIn direction="up" delay={150} duration={800}>
                  <Typography
                    sx={{
                      mb: 4,
                      color: "text.secondary",
                      fontSize: "1.25rem",
                      lineHeight: 1.7,
                    }}
                  >
                    The connected platform for neuro care teams, clients, and carers.
                    Shared goals, timely prompts, and remote monitoring between appointments.
                  </Typography>
                </FadeIn>
                <FadeIn direction="up" delay={300} duration={800}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    justifyContent={{ xs: "center", md: "flex-start" }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      component="a"
                      href={ExternalUrls.dashboardSignup}
                      onClick={() =>
                        capture("cta_clicked", {
                          location: "home_hero",
                          cta_label: "Get Started",
                          cta_href: ExternalUrls.dashboardSignup,
                          cta_type: "primary",
                          cta_external: true,
                        })
                      }
                      sx={{ px: 4 }}
                    >
                      Get Started
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      component={Link}
                      to="/contact-us"
                      onClick={() =>
                        capture("cta_clicked", {
                          location: "home_hero",
                          cta_label: "Book Demo",
                          cta_href: "/contact-us",
                          cta_type: "secondary",
                          cta_external: false,
                        })
                      }
                      sx={{ px: 4 }}
                    >
                      Book Demo
                    </Button>
                  </Stack>
                  {/* rpva item 2: professional route above the fold — the
                      walkthrough found professionals had to scroll-and-hunt. */}
                  <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                    Clinician or case manager?{' '}
                    <Typography
                      component={Link}
                      to="/solutions/professionals"
                      variant="body2"
                      color="primary"
                      onClick={() =>
                        capture("cta_clicked", {
                          location: "home_hero",
                          cta_label: "Professionals find out more",
                          cta_href: "/solutions/professionals",
                          cta_type: "secondary",
                          cta_external: false,
                        })
                      }
                      sx={{ textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                    >
                      Find out how practices use neumind
                    </Typography>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
                    Are you a client or carer?{' '}
                    <Typography
                      component={Link}
                      to="/solutions/clients"
                      variant="body2"
                      color="primary"
                      sx={{ textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                    >
                      Learn how neumind can help you
                    </Typography>
                  </Typography>
                </FadeIn>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <FadeIn direction="left" delay={200} duration={1000}>
                {/* Frame the existing product visual forward: orb-halo brand
                    motif glows behind the mockup, giving navy-tinted depth
                    without clipping the cut-out figure or its floating cards. */}
                <Box sx={{ position: "relative" }}>
                  <BrandBackdrop
                    motif="orb-halo"
                    motifCorner="top-right"
                    motifColor="37,99,235"
                    motifOpacity={0.16}
                    motifSize={460}
                  />
                  <Box sx={{ position: "relative", zIndex: 1 }}>
                    <HeroMockup />
                  </Box>
                </Box>
              </FadeIn>
            </Grid>
          </Grid>
        </Container>
      </Box>



      {/* Trusted By Section */}
      <FadeIn direction="left" delay={200} duration={1000}>
        <LogoMarqueeSection
          tagline="Trusted by top organisations"
          speed={40}
        />
      </FadeIn>

      {/* Professional pain-points section (rpva item 2): the walkthrough
          feedback was that features are listed but the page never answers
          "what will it help me do that's a pain right now?" — lead with the
          pains, resolve each, then route to the professionals page. */}
      <Section py={10} variant="dark">
        <FadeIn>
          <SectionHeader
            overline="For professionals"
            title="The Admin You Didn't Train For"
            subtitle="If coordinating care means chasing message threads, lost diaries and copy-and-paste reporting, this is the part neumind takes off your plate."
            centered
            variant="dark"
            sx={{ maxWidth: 700, mx: "auto", mb: 6 }}
          />
        </FadeIn>
        <Grid container spacing={4}>
          {[
            {
              icon: <ForumIcon sx={{ fontSize: 32 }} />,
              title: "Updates Buried in Message Threads",
              description: "Check-ins from clients, carers and support workers land in one shared record, not scattered across WhatsApp threads you trawl before every meeting.",
              color: BrandColors.error.main,
            },
            {
              icon: <MenuBookIcon sx={{ fontSize: 32 }} />,
              title: "Paper Diaries That Go Missing",
              description: "Reminders, trackers and journals live in the client's app, so the record builds itself between visits. Nothing gets left in a kitchen drawer.",
              color: BrandColors.warning.main,
            },
            {
              icon: <SummarizeIcon sx={{ fontSize: 32 }} />,
              title: "Reports Assembled by Hand",
              description: "Progress is captured as clients go, ready to pull into report-ready summaries for MDTs and funders. No more evenings of copy-and-paste.",
              color: BrandColors.primary.main,
            },
          ].map((item) => (
            <Grid item xs={12} md={4} key={item.title}>
              <InfoCard
                variant="glass"
                title={item.title}
                description={item.description}
                icon={
                  <FeatureIcon
                    icon={item.icon}
                    color={item.color}
                  />
                }
              />
            </Grid>
          ))
          }
        </Grid>
        <FadeIn delay={400}>
          <Box sx={{ mt: 6, textAlign: "center" }}>
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/solutions/professionals"
              endIcon={<ChevronRightIcon />}
              onClick={() =>
                capture("cta_clicked", {
                  location: "home_pain_points",
                  cta_label: "Find out more for professionals",
                  cta_href: "/solutions/professionals",
                  cta_type: "primary",
                  cta_external: false,
                })
              }
              sx={{ px: 4 }}
            >
              Find Out More for Professionals
            </Button>
          </Box>
        </FadeIn>
      </Section>

      {/* Features Section */}
      <Section py={12}>
        <FadeIn>
          <SectionHeader
            overline="Who it's for"
            title="One Platform, Two Interfaces"
            subtitle="A clinician dashboard pairs with a client app to turn rehab goals into actionable tasks. Create plans, track progress, and keep the whole Care Circle aligned."
            centered
            sx={{ maxWidth: 700, mx: "auto", mb: 8 }}
          />
        </FadeIn>

        <Grid container spacing={4}>
          {/* Left column - 2 features + CTA */}
          <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Stack spacing={4} sx={{ flexGrow: 1 }}>
              <FadeIn delay={100} direction="left">
                <InfoCard
                  icon={<FeatureIcon icon={<AutoAwesomeIcon />} color={BrandColors.success.main} />}
                  title="Continuous Care"
                  description="Assign goals and resources, prompt and monitor progress to keep rehab active between appointments."
                />
              </FadeIn>
              <FadeIn delay={200} direction="left">
                <InfoCard
                  icon={<FeatureIcon icon={<ScienceIcon />} color={BrandColors.warning.main} />}
                  title="Evidence-Based Results"
                  description="Aligned to neurorehab best practice with real-world engagement and outcome data underpinning the approach."
                />
              </FadeIn>
            </Stack>
          </Grid>

          {/* Center - audience callouts + Platform Image Composite.
              rpva item 3: the two audience buttons were small and cramped at
              the bottom of the side columns while dead space sat above the
              centre image — moved here, made large, above the image. */}
          <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <FadeIn delay={100} style={{ width: '100%' }}>
              {/* VER-260805 J1.3: filled + chevron-free + top-aligned pair
                  (alignItems flex-start stops the 2-line label offsetting its
                  sibling; labels centre by Button default). */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
                alignItems={{ xs: "stretch", sm: "flex-start" }}
                sx={{ mb: { xs: 3, md: 4 }, px: { xs: 0, md: 2 } }}
              >
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/solutions/professionals"
                  sx={{ flex: { sm: 1 }, py: 1.5, fontWeight: 600 }}
                >
                  Professionals & Practices
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  color="secondary"
                  component={Link}
                  to="/solutions/clients"
                  sx={{ flex: { sm: 1 }, py: 1.5, fontWeight: 600 }}
                >
                  Clients & Caregivers
                </Button>
              </Stack>
            </FadeIn>
            <FadeIn delay={150} style={{ width: '100%' }}>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <ScreenshotShowcase variant="compact" />
              </Box>
            </FadeIn>
          </Grid>

          {/* Right column - 2 features + CTA */}
          <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Stack spacing={4} sx={{ flexGrow: 1 }}>
              <FadeIn delay={100} direction="right">
                <InfoCard
                  icon={<FeatureIcon icon={<PsychologyIcon />} color={BrandColors.info.main} />}
                  title="Designed for Cognition"
                  description="Memory aids, routine planners, and step-based tasks built specifically for cognitive rehabilitation needs."
                />
              </FadeIn>
              <FadeIn delay={200} direction="right">
                <InfoCard
                  icon={<FeatureIcon icon={<GroupsIcon />} color={BrandColors.primary.main} />}
                  title="Collaborative Care Circles"
                  description="Link clients, carers, and clinicians on one plan with shared goals and updates to coordinate support day-to-day."
                />
              </FadeIn>
            </Stack>
          </Grid>
        </Grid>
      </Section >

      {/* Metrics Section */}
      <MetricsSection
        py={8}
        metricIds={['taskAdherence', 'timeSaving', 'independence']}
        alignMetrics="left"
      />

      {/* Our Why Section */}
      < TwoColumnSection
        variant="dark"
        left={
          <>
            <Typography
              variant="overline"
              sx={{
                color: BrandColors.secondary.light,
                fontWeight: 600,
                letterSpacing: 2,
                mb: 2,
                display: "block",
              }}
            >
              Our Why
            </Typography>
            <Typography
              variant="h2"
              sx={{
                color: "white",
                lineHeight: 1.2,
              }}
            >
              We understand,
              <br />
              we've lived it
            </Typography>
          </>
        }
        right={
          <>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.85)",
                fontSize: "1.125rem",
                lineHeight: 1.8,
                mb: 3,
              }}
            >
              Born from the experience of identical twin brothers and one
              life-changing traumatic brain injury. We understand the journey,
              the traumas and challenges because we have lived them,
              co-created with clients and families and clinicians so neumind
              fits real life and supports the moments that matter.
            </Typography>
            <Button
              variant="outlined"
              component={Link}
              to="/about"
              size="large"
              sx={{
                borderColor: "rgba(255,255,255,0.3)",
                color: "white",
                "&:hover": {
                  borderColor: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Our story →
            </Button>
          </>
        }
      />

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* CTA Section */}
      <Box sx={{ py: 12, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <CallToActionCard
            location="home_bottom_cta"
            title="Ready to Transform Care?"
            primaryCTA={{ label: "Get Started", href: ExternalUrls.dashboardSignup, external: true }}
            secondaryCTA={{ label: "Book Demo", href: "/contact-us" }}
          />
        </Container>
      </Box>
    </>
  );
}
